import { useEffect, useState, type RefObject } from "react";

/** The shown tooltip: its label, anchor position, and which side it sits on. */
interface FieldTooltipState {
  label: string;
  top: number;
  left: number;
  placement: "above" | "below";
}

/** Vertical gap between the tooltip (with its arrow) and the hovered field. */
const ANCHOR_GAP_PX = 6;

/** Room the tooltip needs above a field before it flips below it. */
const FLIP_THRESHOLD_PX = 40;

interface FieldTooltipProps {
  /** Ref to the scrollable preview container the tooltip is positioned in. */
  containerRef: RefObject<HTMLDivElement>;
}

/**
 * One shared hover tooltip for every bound field on the editor canvas. Field
 * values render with a `data-field-title` attribute naming their field; a
 * delegated listener on the preview container floats this styled tip (in the
 * editor's blue binding color, with an anchoring arrow) centered above whichever
 * one the pointer is over, flipping below when the field sits at the top of
 * the visible canvas.
 * @param containerRef - The scrollable preview container
 * @returns The floating tooltip, or null when nothing is hovered
 */
export function FieldTooltip({ containerRef }: FieldTooltipProps) {
  const [tip, setTip] = useState<FieldTooltipState | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const onMouseOver = (event: MouseEvent) => {
      const target =
        event.target instanceof Element
          ? event.target.closest("[data-field-title]")
          : null;
      const label = target?.getAttribute("data-field-title");
      if (!target || !label) {
        setTip(null);
        return;
      }
      const rect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const placement =
        rect.top - containerRect.top < FLIP_THRESHOLD_PX ? "below" : "above";
      setTip({
        label,
        top:
          (placement === "above" ? rect.top : rect.bottom) -
          containerRect.top +
          container.scrollTop,
        left:
          rect.left +
          rect.width / 2 -
          containerRect.left +
          container.scrollLeft,
        placement,
      });
    };
    const hide = () => setTip(null);
    container.addEventListener("mouseover", onMouseOver);
    container.addEventListener("mouseleave", hide);
    container.addEventListener("scroll", hide);
    return () => {
      container.removeEventListener("mouseover", onMouseOver);
      container.removeEventListener("mouseleave", hide);
      container.removeEventListener("scroll", hide);
    };
  }, [containerRef]);

  if (!tip) {
    return null;
  }
  const above = tip.placement === "above";
  return (
    <div
      className={`pointer-events-none absolute z-50 -translate-x-1/2 animate-in fade-in-0 zoom-in-95 duration-150 motion-reduce:animate-none ${
        above
          ? "-translate-y-full slide-in-from-bottom-1"
          : "slide-in-from-top-1"
      }`}
      style={{
        top: above ? tip.top - ANCHOR_GAP_PX : tip.top + ANCHOR_GAP_PX,
        left: tip.left,
      }}
    >
      <div className="whitespace-nowrap rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white shadow-md shadow-blue-600/20">
        {tip.label}
      </div>
      <div
        className={`absolute left-1/2 size-2 -translate-x-1/2 rotate-45 bg-blue-600 ${
          above ? "-bottom-0.5" : "-top-0.5"
        }`}
      />
    </div>
  );
}
